import { FC, useEffect, useMemo } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { CollectionIcon, MailIcon } from '@heroicons/react/outline';
import classNames from 'classnames';

import { Feature } from '../../../../generated/graphql';
import { useFeatureFlags } from '../../../../contexts/FeatureFlagContext';
import MailSequenceTemplates from '../MailSequenceTemplates';
import MailTemplates from './MailTemplates';

type TemplatesSection = 'mail' | 'mail-sequence';

const MAIL_PATH = '/automations/templates/mail';
const MAIL_SEQUENCE_PATH = '/automations/templates/mail-sequence';

const Templates: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    enabledFeatures,
    loading: featuresLoading,
    error: featuresError,
    isFeatureEnabled,
  } = useFeatureFlags();
  const featuresLoaded = enabledFeatures !== undefined;
  const isMailSequencesEnabled =
    !featuresError && isFeatureEnabled(Feature.MailSequences);

  const navItems = useMemo(
    () => [
      {
        id: 'mail' as const,
        label: 'Mail',
        path: MAIL_PATH,
        Icon: MailIcon,
      },
      ...(isMailSequencesEnabled
        ? [
            {
              id: 'mail-sequence' as const,
              label: 'Mail Sequence',
              path: MAIL_SEQUENCE_PATH,
              Icon: CollectionIcon,
            },
          ]
        : []),
    ],
    [isMailSequencesEnabled],
  );

  const activeSection: TemplatesSection = location.pathname.includes(
    '/mail-sequence',
  )
    ? 'mail-sequence'
    : 'mail';

  useEffect(() => {
    if (activeSection !== 'mail-sequence') {
      return;
    }
    if (featuresLoading && !featuresLoaded) {
      return;
    }
    if (featuresError || !isMailSequencesEnabled) {
      navigate(MAIL_PATH, { replace: true });
    }
  }, [
    activeSection,
    featuresError,
    featuresLoaded,
    featuresLoading,
    isMailSequencesEnabled,
    navigate,
  ]);

  // Only block the Mail Sequence section while flags load; Mail templates stay usable.
  if (activeSection === 'mail-sequence') {
    if (featuresLoading && !featuresLoaded) {
      return null;
    }
    if (featuresError || !isMailSequencesEnabled) {
      return <Navigate to={MAIL_PATH} replace />;
    }
  }

  return (
    <div className="mt-4 flex min-h-0 flex-1 gap-6">
      <div className="flex w-64 shrink-0 flex-col border-r border-neutral-100 pr-4">
        <div className="space-y-2">
          {navItems.map(({ id, label, path, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => navigate(path)}
              className={classNames(
                'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-body transition-colors',
                activeSection === id
                  ? 'bg-secondary-50 font-medium text-neutral-150'
                  : 'text-neutral-120 hover:bg-neutral-50 hover:text-neutral-150',
              )}
            >
              <Icon className="h-5 w-5 shrink-0" aria-hidden />
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="min-h-0 min-w-0 flex-1 overflow-y-auto scrollbar-hide">
        {activeSection === 'mail-sequence' ? (
          <MailSequenceTemplates />
        ) : (
          <MailTemplates />
        )}
      </div>
    </div>
  );
};

export default Templates;
